import axiosInstance from '@/plugins/axios';
import apiEndpoints from '@/modules/data-layer/config/endpoint';

const longRunningListRequestConfig = {
  timeout: 120000,
  disableAutoRetry: true,
};

let locationsRequest = null;
let locationsResponseCache = null;
let datasourceSpecificRequest = null;
let datasourceSpecificResponseCache = null;
let indicatorsAndSourcesRequest = null;
let indicatorsAndSourcesResponseCache = null;

export const allLocations = async () => {
  if (locationsResponseCache) {
    return locationsResponseCache;
  }

  if (!locationsRequest) {
    locationsRequest = axiosInstance
      .get(apiEndpoints.getLocation, longRunningListRequestConfig)
      .then((response) => {
        locationsResponseCache = response;
        return response;
      })
      .finally(() => {
        locationsRequest = null;
      });
  }

  return locationsRequest;
};

export const latestData = () => axiosInstance.get('https://msdatapi.e4eweb.space/api/data/latest');
export const datasourceSpecific = async () => {
  if (datasourceSpecificResponseCache) {
    return datasourceSpecificResponseCache;
  }

  if (!datasourceSpecificRequest) {
    datasourceSpecificRequest = axiosInstance
      .get(apiEndpoints.getDSI, longRunningListRequestConfig)
      .then((response) => {
        datasourceSpecificResponseCache = response;
        return response;
      })
      .finally(() => {
        datasourceSpecificRequest = null;
      });
  }

  return datasourceSpecificRequest;
};

export const fetchDemographics = async (params, locationId) => {
  const values = await Promise.all(
    params.map(async (el) => {
      const periodRequests = [el.year, el.previousYear]
        .filter((period, index, array) => period && array.indexOf(period) === index)
        .map((period) => axiosInstance.get(
          `${apiEndpoints.getData}indicator=${el.indicatorId}&datasource=${el.sourceId}&period=${period}&location=${locationId}`,
        ));

      if (periodRequests.length) {
        const periodResponses = await Promise.all(periodRequests);
        return {
          data: {
            results: periodResponses
              .map((response) => response?.data?.results?.[0] || null)
              .filter(Boolean),
          },
        };
      }

      return axiosInstance.get(
        `${apiEndpoints.getData}indicator=${el.indicatorId}&datasource=${el.sourceId}&location=${locationId}`,
      );
    }),
  );
  return values;
};

export const hydratePayloadWithPeriods = async (
  params,
  locationId,
  {
    indicatorKey = 'indicator',
    datasourceKey = 'dataSource',
    yearKey = 'year',
    previousYearKey = null,
  } = {},
) => {
  const latestActualYear = new Date().getFullYear() - 1;
  const sortPeriods = (periods = []) => periods.filter((period) => {
    const numericPeriod = Number(period);

    if (Number.isNaN(numericPeriod)) {
      return true;
    }

    return numericPeriod <= latestActualYear;
  }).sort((left, right) => {
    const leftNumber = Number(left);
    const rightNumber = Number(right);

    if (!Number.isNaN(leftNumber) && !Number.isNaN(rightNumber)) {
      return rightNumber - leftNumber;
    }

    return String(right).localeCompare(String(left), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  });

  const periodResponses = await Promise.all(
    params.map(async (item) => {
      try {
        return await axiosInstance.get(
          `period-data/?datasource=${item[datasourceKey]}&indicator=${item[indicatorKey]}&location=${locationId}`,
        );
      } catch (error) {
        return null;
      }
    }),
  );

  return params.map((item, index) => {
    const periods = sortPeriods(periodResponses[index]?.data?.periods || []);

    if (!periods.length) {
      return { ...item };
    }

    const nextItem = {
      ...item,
      [yearKey]: periods[0] || item[yearKey],
    };

    if (previousYearKey && periods[1]) {
      nextItem[previousYearKey] = periods[1];
    }

    return nextItem;
  });
};

export const getRegularData = async (params, locationId) => {
  const national = await Promise.all(
    params.map((el) => axiosInstance.get(
      `${apiEndpoints.getData}indicator=${el.indicator}&datasource=${el.dataSource}&period=${el.year}&location=1`,
    )),
  );

  const newNational = national.map((el) => el.data);
  if (locationId !== 1) {
    const state = await Promise.all(
      params.map((el) => axiosInstance.get(
        `${apiEndpoints.getData}indicator=${el.indicator}&datasource=${el.dataSource}&period=${el.year}&location=${locationId}`,
      )),
    );
    const newState = state.map((el) => el.data);
    return { newNational, newState };
  }
  return { newNational };
};

export const getAllData = async (params) => {
  const allDataPoints = await Promise.all(
    params.map((el) => axiosInstance.get(
      `${apiEndpoints.getData}indicator=${el.indicator}&datasource=${el.dataSource}&location=${el.locationId}`,
    )),
  );

  return { allDataPoints };
};

export const getAllDataWithPeriods = async (params) => {
  const allDataPoints = await Promise.all(
    params.map((el) => axiosInstance.get(
      `${apiEndpoints.getData}indicator=${el.indicator}&datasource=${el.dataSource}&period=${el.year}&location=${el.locationId}`,
    )),
  );

  return { allDataPoints };
};

export const getIndicatorsAndSources = async () => {
  if (indicatorsAndSourcesResponseCache) {
    return indicatorsAndSourcesResponseCache;
  }

  if (!indicatorsAndSourcesRequest) {
    indicatorsAndSourcesRequest = Promise.all([
      axiosInstance.get(apiEndpoints.getIndicator, longRunningListRequestConfig),
      axiosInstance.get(apiEndpoints.getDataSource, longRunningListRequestConfig),
    ])
      .then(([theIndicators, theSources]) => {
        indicatorsAndSourcesResponseCache = { theIndicators, theSources };
        return indicatorsAndSourcesResponseCache;
      })
      .finally(() => {
        indicatorsAndSourcesRequest = null;
      });
  }

  return indicatorsAndSourcesRequest;
};
